import {
  animate,
  motion,
  MotionValue,
  useAnimationFrame,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  useSpring,
  useTransform,
} from "motion/react"
import { PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"

type Props = {
  speed?: number
  items: ReactNode[]
  aspect: number
  paused?: boolean
  direction?: "left" | "right"
}

export function AutoCarousel(props: Props) {
  // Auto-progressing clock
  const autoSpeed = useMotionValue(1)
  const clock = useMotionValue(0)

  // Drag progress
  const dragProgress = useMotionValue(0)

  // Lerp wheel target
  const wheelTarget = useMotionValue(0)
  const wheelProgress = useSpring(wheelTarget, { stiffness: 1000, damping: 100 })

  // In view?
  const ref = useRef<HTMLDivElement>(null!)
  const inView = useInView(ref)

  // Calculate progress value every frame by combining all values above
  const progress = useMotionValue(0)
  useAnimationFrame((t, dt) => {
    if (!inView) return
    clock.set(clock.get() + (dt / 1000) * autoSpeed.get() * (props.direction === "left" ? 1 : -1))
    // progress.set(clock.get() - dragProgress.get() / size.width - wheelProgress.get() / size.width)
    progress.set(clock.get() % props.items.length)
  })

  useEffect(() => {
    if (props.paused) {
      animate(autoSpeed, 0)
    } else {
      animate(autoSpeed, props.speed ?? 0.1)
    }
  }, [props.paused, props.speed])

  const size = useMemo(() => ({ width: 600, height: 0 }), [])

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      size.width = entry.contentRect.width
      size.height = entry.contentRect.height
    })
    if (ref.current) {
      ro.observe(ref.current)
    }
    return () => ro.disconnect()
  }, [])

  // Handle wheel events...
  // useEffect(() => {
  //   let timer: NodeJS.Timeout
  //   const onWheel = (e: WheelEvent) => {
  //     if (e.deltaMode === 0 && e.deltaX !== 0) {
  //       e.preventDefault()
  //       wheelTarget.set(wheelTarget.get() - e.deltaX)
  //       animate(autoSpeed, 0, {
  //         type: "tween",
  //         ease: "easeOut",
  //         duration: 0.3,
  //       })
  //       clearTimeout(timer)
  //       timer = setTimeout(() => {
  //         animate(autoSpeed, Math.sign(e.deltaX), { duration: 0.3 })
  //       })
  //     }
  //   }
  //   const el = ref.current
  //   el.addEventListener("wheel", onWheel, { passive: false })
  //   return () => el.removeEventListener("wheel", onWheel)
  // }, [])

  const items = props.items || []

  if (items.length === 0) return null

  // make sure there's at least 4
  if (items.length < 4) {
    const duplicates = Math.ceil(4 / items.length) - 1
    for (let i = 0; i < duplicates; i++) {
      items.push(...items)
    }
  }

  return (
    <motion.div
      ref={ref}
      className="absolute inset-0 overflow-hidden isolate"
      // drag="x"
      // _dragX={dragProgress}
      // dragMomentum={true}
      // onDragStart={() => {
      //   animate(autoSpeed, 0)
      // }}
      // onDragEnd={() => {
      //   animate(autoSpeed, dragProgress.getVelocity() < 1 ? -1 : 1)
      // }}
      // onDrag={() => {}}
      style={
        {
          "--aspect": String(props.aspect),
        } as any
      }
    >
      <div className="absolute h-full will-change-transform">
        {items.map((item, i) => (
          <Slide key={i} index={i} progress={progress} totalItems={items.length}>
            {item}
          </Slide>
        ))}
        {items.map((item, i) => (
          <Slide key={i} index={i} progress={progress} totalItems={items.length} offsetIndex={items.length}>
            {item}
          </Slide>
        ))}
      </div>
    </motion.div>
  )
}

// Slide component, which updates its position based on progress and conditionally renders
function Slide(
  props: PropsWithChildren<{ index: number; progress: MotionValue<number>; totalItems: number; offsetIndex?: number }>,
) {
  const offset = useTransform(props.progress, (value) => {
    let offset = value - props.index + (props.offsetIndex ?? 0)
    if (offset <= 0) {
      offset += props.totalItems
    }
    offset = offset - 1

    return offset
  })
  const [render, setRender] = useState(true)
  useMotionValueEvent(offset, "change", (value) => {
    setRender(value >= -1 && value < 3)
  })

  return (
    render && (
      <motion.div
        className="absolute top-0 left-0 h-full bg-red aspect-[--aspect]"
        style={
          {
            "--offset": offset,
            "--index": props.index,
            transform: "translateX(calc(var(--offset) * 100%))",
          } as any
        }
      >
        {props.children}
      </motion.div>
    )
  )
}
